// src/pages/validasi/ValidasiLevel2.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, RotateCcw, Eye, AlertTriangle, Filter, Search,
  Clock, ChevronDown, ChevronUp, User, Building2,
  Shield, TrendingUp, TrendingDown, Calendar, History
} from 'lucide-react';
import { Button, Badge, Card, Breadcrumb, Modal, Textarea, Select, Input } from '../../components/ui';
import { LaporanMasalah } from '../../types';
import { Link } from 'react-router-dom';
import { validasiService, ValidasiFilter } from '../../services/validasi';
import { useRole } from '../../hooks/useRole';
import { getErrorMessage, logError } from '../../utils/errorHandler';
import toast from 'react-hot-toast';

type ValidasiAction = 'approve' | 'reject' | 'revisi';

interface Stats {
  pending_level1: number;
  pending_level2: number;
  approved_today_l1: number;
  approved_today_l2: number;
  rejected_today_l1: number;
  rejected_today_l2: number;
}

const ValidasiLevel2: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
  const [laporan, setLaporan] = useState<LaporanMasalah[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLaporan, setSelectedLaporan] = useState<LaporanMasalah | null>(null);
  const [action, setAction] = useState<ValidasiAction | null>(null);
  const [catatan, setCatatan] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ValidasiFilter>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);

  const role = useRole();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: ValidasiFilter = { ...filters };
      if (searchTerm) params.search = searchTerm;

      let res;
      if (activeTab === 'pending') {
        params.status = filters.status || 'validasi_l1';
        res = await validasiService.getPendingLevel2(params);
      } else {
        res = await validasiService.getRiwayatLevel2(params);
      }

      const data = (res as any)?.data || res;
      setLaporan(Array.isArray(data) ? data : (data?.data || []));
    } catch (err) {
      console.error('Failed to load data L2:', err);
      setLaporan([]);
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm, activeTab]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await validasiService.getStats();
      const data = (res as any)?.data || res;
      setStats(data);
    } catch (err) { console.error('Failed to load stats:', err); }
  }, []);

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [fetchData, fetchStats, activeTab]);

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const openModal = (lap: LaporanMasalah, act: ValidasiAction) => { setSelectedLaporan(lap); setAction(act); setCatatan(''); };
  const closeModal = () => { setSelectedLaporan(null); setAction(null); setCatatan(''); };

  const handleValidasi = async () => {
    if (!action) return;
    if ((action === 'reject' || action === 'revisi') && !catatan.trim()) { toast.error('Catatan wajib diisi untuk penolakan/revisi'); return; }
    setProcessing(true);
    try {
      await validasiService.validateLevel2(selectedLaporan!.id, { action, catatan });
      toast.success(action === 'approve' ? 'Laporan berhasil disetujui ke RAPIM' : action === 'reject' ? 'Laporan ditolak' : 'Laporan dikembalikan ke Level 1');
      closeModal();
      fetchData();
      fetchStats();
    } catch (e: any) {
      toast.error(getErrorMessage(e, 'Gagal memproses validasi'));
      logError('ValidasiLevel2.handleValidasi', e);
    }
    finally { setProcessing(false); }
  };

  const toggleSelect = (id: number) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    if (selectedIds.length === laporan.length) { setSelectedIds([]); } else { setSelectedIds(laporan.map(l => l.id)); }
  };

  const handleBatchApprove = async () => {
    if (selectedIds.length === 0) return;
    setProcessing(true);
    try {
      await validasiService.batchApproveLevel2(selectedIds);
      toast.success(`${selectedIds.length} laporan berhasil disetujui`);
      setSelectedIds([]);
      fetchData();
      fetchStats();
    } catch (e) { toast.error('Gagal memproses batch validasi'); }
    finally { setProcessing(false); }
  };

  const handleSearch = () => { fetchData(); };

  if (!role.canValidateL2) {
    return (
      <div className="space-y-6 animate-in">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Validasi Level 2' }]} />
        <Card className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-warning" />
          <p className="text-lg font-bold">Akses Terbatas</p>
          <p className="text-sm text-neutral-text-secondary mt-1">Hanya Wakil Rektor yang dapat mengakses halaman validasi Level 2.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Validasi Level 2' }]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Validasi Level 2</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Persetujuan Wakil Rektor</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'pending' && selectedIds.length > 0 && (
            <Button variant="success" size="sm" onClick={handleBatchApprove} loading={processing} icon={<CheckCircle className="w-4 h-4" />}>
              Setujui {selectedIds.length} Laporan
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} icon={<Filter className="w-4 h-4" />}>
            Filter
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="cursor-pointer" onClick={() => setActiveTab('pending')}>
            <Card className={`!p-4 transition-all ${activeTab === 'pending' ? 'ring-2 ring-info bg-info/5 shadow-sm' : 'hover:bg-neutral-bg'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-info/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-info" />
                </div>
                <div>
                  <p className="text-2xl font-black text-neutral-text-primary">{stats.pending_level2}</p>
                  <p className="text-xs text-neutral-text-secondary">Menunggu Validasi</p>
                </div>
              </div>
            </Card>
          </div>
          <div className="cursor-pointer" onClick={() => setActiveTab('history')}>
            <Card className={`!p-4 transition-all ${activeTab === 'history' ? 'ring-2 ring-success bg-success/5 shadow-sm' : 'hover:bg-neutral-bg'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-black text-success">{stats.approved_today_l2}</p>
                  <p className="text-xs text-neutral-text-secondary">Disetujui Hari Ini</p>
                </div>
              </div>
            </Card>
          </div>
          <div className="cursor-pointer" onClick={() => setActiveTab('history')}>
            <Card className="!p-4 transition-all hover:bg-neutral-bg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger/10 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-danger" />
                </div>
                <div>
                  <p className="text-2xl font-black text-danger">{stats.rejected_today_l2}</p>
                  <p className="text-xs text-neutral-text-secondary">Ditolak Hari Ini</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-neutral-border">
        <button
          className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'pending' ? 'border-teal-main text-teal-main bg-teal-main/5' : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'}`}
          onClick={() => setActiveTab('pending')}
        >
          Menunggu ({stats?.pending_level2 || 0})
        </button>
        <button
          className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'history' ? 'border-teal-main text-teal-main bg-teal-main/5' : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'}`}
          onClick={() => setActiveTab('history')}
        >
          <History className="w-4 h-4" />
          Riwayat
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="!p-4 animate-in fade-in slide-in-from-top-1">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <div className="flex gap-2">
                <Input placeholder="Cari judul atau kode..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                <Button variant="primary" size="sm" onClick={handleSearch} icon={<Search className="w-4 h-4" />}>Cari</Button>
              </div>
            </div>
            <Select
              value={filters.risiko || ''}
              onChange={(value) => setFilters({ ...filters, risiko: value || undefined })}
              options={[
                { value: '', label: 'Semua Risiko' },
                { value: 'rendah', label: 'Rendah' },
                { value: 'sedang', label: 'Sedang' },
                { value: 'tinggi', label: 'Tinggi' },
                { value: 'kritis', label: 'Kritis' },
              ]}
            />
            <Button variant="ghost" size="sm" onClick={() => { setFilters({}); setSearchTerm(''); }}>Reset</Button>
          </div>
        </Card>
      )}

      {/* List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-main mx-auto mb-3"></div>
              <p className="text-sm text-neutral-text-secondary">Memuat data...</p>
            </div>
          </div>
        ) : laporan.length > 0 ? (
          <>
            {activeTab === 'pending' && (
              <div className="flex items-center gap-2 px-1">
                <input type="checkbox" checked={selectedIds.length === laporan.length && laporan.length > 0} onChange={toggleSelectAll}
                  className="rounded border-neutral-border text-teal-main focus:ring-teal-light" />
                <span className="text-xs text-neutral-text-secondary">Pilih semua ({laporan.length})</span>
              </div>
            )}
            {laporan.map((lap) => {
              const isExpanded = expandedId === lap.id;

              // Untuk tab riwayat: ambil riwayat dari field riwayat_transisi yang diisi backend (dari state_transitions)
              const transisiL2 = activeTab === 'history' ? (lap as any).riwayat_transisi : null;
              // to_state: antrean_rapim = approve, ditolak_l2 = reject, validasi_l1 = revisi
              const historyIsApproved = transisiL2 && (transisiL2.to_state === 'antrean_rapim' || transisiL2.action === 'approve');
              const historyStatus = historyIsApproved ? 'success' : 'danger';

              return (
                <Card
                  key={lap.id}
                  hover
                  className={`border-l-4 transition-all ${activeTab === 'history'
                    ? (historyStatus === 'success' ? 'border-l-success' : 'border-l-danger')
                    : 'border-l-info'
                    }`}
                >
                  <div className="flex items-start gap-4">
                    {activeTab === 'pending' && (
                      <input type="checkbox" checked={selectedIds.includes(lap.id)} onChange={() => toggleSelect(lap.id)}
                        className="mt-1 rounded border-neutral-border text-teal-main focus:ring-teal-light" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs font-mono text-teal-main">{lap.kode}</span>
                            <Badge variant="risk" risk={lap.tingkat_risiko} />
                            {/* Badge status riwayat */}
                            {activeTab === 'history' && transisiL2 && (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${historyStatus === 'success' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                                {historyIsApproved ? '✓ Disetujui ke RAPIM' : transisiL2.to_state === 'validasi_l1' ? '↩ Dikembalikan ke L1' : '✗ Ditolak'}
                              </span>
                            )}
                            {/* Badge L1 Approved untuk pending tab */}
                            {activeTab === 'pending' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-success/10 text-success font-medium flex items-center gap-1">
                                <Shield className="w-3 h-3" /> L1 Approved
                              </span>
                            )}
                            {lap.kategori && (
                              <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: lap.kategori?.color }}>
                                {lap.kategori?.nama}
                              </span>
                            )}
                          </div>
                          <Link to={`/laporan/${lap.id}`} className="text-base font-bold text-neutral-text-primary hover:text-teal-main transition-colors">
                            {lap.judul}
                          </Link>
                          <div className="flex items-center gap-4 mt-2 text-xs text-neutral-text-secondary flex-wrap">
                            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {lap.user?.name}</span>
                            <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {lap.unit?.name}</span>
                            {lap.target_selesai && (
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Target: {new Date(lap.target_selesai).toLocaleDateString('id-ID')}</span>
                            )}
                            {/* Tanggal diproses untuk tab riwayat */}
                            {activeTab === 'history' && transisiL2 && (
                              <span className="flex items-center gap-1 font-medium text-teal-main">
                                <Clock className="w-3 h-3" />
                                {historyIsApproved ? 'Disetujui' : 'Diproses'}: {new Date(transisiL2.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                          {/* Catatan dari riwayat transisi */}
                          {activeTab === 'history' && transisiL2?.notes && (
                            <p className="mt-2 text-xs text-neutral-text-secondary italic bg-neutral-bg p-2 rounded-lg border-l-2 border-neutral-border">
                              "{transisiL2.notes}"
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => toggleExpand(lap.id)} icon={isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}>
                            {isExpanded ? 'Tutup' : 'Detail'}
                          </Button>
                          {activeTab === 'pending' ? (
                            <>
                              <Button variant="success" size="sm" onClick={() => openModal(lap, 'approve')} icon={<CheckCircle className="w-4 h-4" />}>Setuju</Button>
                              <Button variant="outline" size="sm" onClick={() => openModal(lap, 'revisi')} icon={<RotateCcw className="w-4 h-4" />}>Revisi</Button>
                              <Button variant="danger" size="sm" onClick={() => openModal(lap, 'reject')} icon={<XCircle className="w-4 h-4" />}>Tolak</Button>
                            </>
                          ) : (
                            <Link to={`/laporan/${lap.id}`}>
                              <Button variant="primary" size="sm" icon={<Eye className="w-4 h-4" />}>Lihat Detail</Button>
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Expanded Detail */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-neutral-border space-y-4 animate-in slide-in-from-top-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-bold text-neutral-text-secondary uppercase tracking-wider mb-1">Deskripsi Masalah</p>
                              <p className="text-sm text-neutral-text-primary line-clamp-4">{lap.deskripsi || <span className="italic text-neutral-text-secondary">Tidak ada deskripsi</span>}</p>
                            </div>
                            <div>
                              <p className="text-xs font-bold text-neutral-text-secondary uppercase tracking-wider mb-1">Dampak</p>
                              <p className="text-sm text-neutral-text-primary line-clamp-4">{lap.dampak || <span className="italic text-neutral-text-secondary">Tidak ada informasi dampak</span>}</p>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Link to={`/laporan/${lap.id}`}>
                              <Button variant="ghost" size="sm" icon={<Eye className="w-4 h-4" />}>Lihat Detail Lengkap</Button>
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </>
        ) : (
          <Card className="text-center py-16">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: activeTab === 'history' ? 'rgba(0,128,128,0.1)' : 'rgba(0,180,100,0.1)' }}>
              {activeTab === 'history'
                ? <History className="w-8 h-8 text-teal-main" />
                : <CheckCircle className="w-8 h-8 text-success" />
              }
            </div>
            <p className="text-xl font-black text-neutral-text-primary">
              {activeTab === 'pending' ? 'Semua laporan sudah divalidasi L2' : 'Belum ada riwayat validasi L2'}
            </p>
            <p className="text-sm text-neutral-text-secondary mt-2 max-w-xs mx-auto">
              {activeTab === 'pending'
                ? 'Tidak ada laporan baru yang menunggu persetujuan Warek saat ini.'
                : 'Laporan yang telah Anda setujui atau tolak akan muncul di sini.'}
            </p>
            {searchTerm && (
              <Button variant="ghost" size="sm" className="mt-4" onClick={() => { setSearchTerm(''); setFilters({}); }}>Bersihkan Pencarian</Button>
            )}
          </Card>
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={!!selectedLaporan && !!action}
        onClose={closeModal}
        title={
          action === 'approve' ? 'Konfirmasi: Setujui Level 2' :
            action === 'reject' ? 'Konfirmasi: Tolak Laporan' :
              'Konfirmasi: Kembalikan ke L1'
        }
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={closeModal}>Batal</Button>
            <Button
              variant={action === 'approve' ? 'success' : action === 'reject' ? 'danger' : 'primary'}
              onClick={handleValidasi}
              loading={processing}
            >
              {action === 'approve' ? 'Setujui Level 2' : action === 'reject' ? 'Tolak Laporan' : 'Kembalikan ke Level 1'}
            </Button>
          </>
        }
      >
        {selectedLaporan && (
          <div className="space-y-4">
            {/* Info banner */}
            <div className={`p-3 rounded-xl border text-sm font-medium flex items-start gap-3 ${action === 'approve' ? 'bg-success/5 border-success/20 text-success' :
                action === 'reject' ? 'bg-danger/5 border-danger/20 text-danger' :
                  'bg-warning/5 border-warning/20 text-warning'
              }`}>
              {action === 'approve' && <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" />}
              {action === 'reject' && <XCircle className="w-5 h-5 shrink-0 mt-0.5" />}
              {action === 'revisi' && <RotateCcw className="w-5 h-5 shrink-0 mt-0.5" />}
              <span>
                {action === 'approve' && 'Anda akan menyetujui laporan ini. Laporan akan masuk ke antrean RAPIM.'}
                {action === 'reject' && 'Laporan akan ditolak dan dikembalikan ke pelapor.'}
                {action === 'revisi' && 'Laporan akan dikembalikan ke Dekan/Kalem untuk direvisi ulang.'}
              </span>
            </div>

            {/* Report card */}
            <div className="p-4 bg-neutral-bg rounded-xl space-y-2">
              <p className="text-xs font-mono text-teal-main">{selectedLaporan.kode}</p>
              <p className="text-sm font-bold text-neutral-text-primary">{selectedLaporan.judul}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="risk" risk={selectedLaporan.tingkat_risiko} />
                <span className="text-xs text-neutral-text-secondary flex items-center gap-1"><Building2 className="w-3 h-3" /> {selectedLaporan.unit?.name}</span>
                <span className="text-xs text-neutral-text-secondary flex items-center gap-1"><User className="w-3 h-3" /> {selectedLaporan.user?.name}</span>
              </div>
              {selectedLaporan.deskripsi && (
                <p className="text-xs text-neutral-text-secondary mt-2 line-clamp-3">{selectedLaporan.deskripsi}</p>
              )}
            </div>

            {/* Validation path (only for approve) */}
            {action === 'approve' && (
              <div className="flex items-center justify-center gap-2 py-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold">
                  <CheckCircle className="w-3.5 h-3.5" /> Dekan ✓
                </div>
                <div className="w-8 h-0.5 bg-success" />
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold">
                  <CheckCircle className="w-3.5 h-3.5" /> Warek Setuju
                </div>
                <div className="w-8 h-0.5 bg-neutral-border" />
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-bg text-neutral-text-secondary text-xs font-bold">
                  <Clock className="w-3.5 h-3.5" /> RAPIM
                </div>
              </div>
            )}

            <Textarea
              label={action === 'approve' ? 'Catatan (opsional)' : 'Catatan (wajib)'}
              placeholder={
                action === 'approve' ? 'Tambahkan catatan jika perlu...' :
                  action === 'reject' ? 'Jelaskan alasan penolakan...' :
                    'Jelaskan apa yang perlu direvisi...'
              }
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              required={action !== 'approve'}
              rows={3}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ValidasiLevel2;